import GameState from './GameState.js';

class Game {
    constructor() {
        this.gameState = new GameState();
        this.elements = {};
        this.lastUpdate = Date.now();
        this.lastGemGeneration = Date.now();
        
        this.initializeElements();
        this.setupEventListeners();
        this.startGameLoop();
        this.updateUI();
    }
    
    initializeElements() {
        this.elements = {
            // Core displays
            moneyDisplay: document.getElementById('money-display'),
            mpsDisplay: document.getElementById('mps-display'),
            clickValueDisplay: document.getElementById('click-value-display'),
            gemsDisplay: document.getElementById('gems-display'),
            mainClickBtn: document.getElementById('main-click-btn'),
            
            // Prestige layers
            prestigePoints1: document.getElementById('prestige-points-1'),
            prestigePoints2: document.getElementById('prestige-points-2'),
            prestigePoints3: document.getElementById('prestige-points-3'),
            prestigeBtn1: document.getElementById('prestige-btn-1'),
            prestigeBtn2: document.getElementById('prestige-btn-2'),
            prestigeBtn3: document.getElementById('prestige-btn-3'),
            
            // Upgrade containers
            basicUpgrades: document.getElementById('basic-upgrades'),
            businessUpgrades: document.getElementById('business-upgrades'),
            premiumUpgrades: document.getElementById('premium-upgrades'),
            
            // Settings
            settingsBtn: document.getElementById('settings-btn'),
            settingsPanel: document.getElementById('settings-panel'),
            closeSettings: document.getElementById('close-settings'),
            soundToggle: document.getElementById('sound-toggle'),
            autosaveToggle: document.getElementById('autosave-toggle'),
            resetGameBtn: document.getElementById('reset-game-btn')
        };
    }
    
    setupEventListeners() {
        // Main click button
        this.elements.mainClickBtn.addEventListener('click', () => {
            this.handleMainClick();
        });
        
        // Prestige buttons
        this.elements.prestigeBtn1.addEventListener('click', () => {
            this.handlePrestige('business');
        });
        
        this.elements.prestigeBtn2.addEventListener('click', () => {
            this.handlePrestige('innovation');
        });
        
        this.elements.prestigeBtn3.addEventListener('click', () => {
            this.handlePrestige('legacy');
        });
        
        // Settings
        this.elements.settingsBtn.addEventListener('click', () => {
            this.elements.settingsPanel.classList.remove('hidden');
        });
        
        this.elements.closeSettings.addEventListener('click', () => {
            this.elements.settingsPanel.classList.add('hidden');
        });
        
        this.elements.soundToggle.addEventListener('click', () => {
            this.toggleSound();
        });
        
        this.elements.autosaveToggle.addEventListener('click', () => {
            this.toggleAutoSave();
        });
        
        this.elements.resetGameBtn.addEventListener('click', () => {
            this.gameState.resetGame();
        });
        
        // Close settings when clicking outside
        this.elements.settingsPanel.addEventListener('click', (e) => {
            if (e.target === this.elements.settingsPanel) {
                this.elements.settingsPanel.classList.add('hidden');
            }
        });
        
        // Auto-save every 30 seconds
        setInterval(() => {
            if (this.gameState.settings.autoSaveEnabled) {
                this.gameState.saveGame();
            }
        }, 30000);
        
        // Save on page unload
        window.addEventListener('beforeunload', () => {
            this.gameState.saveGame();
        });
    }
    
    handleMainClick() {
        const earnedMoney = this.gameState.clickValue;
        this.gameState.money += earnedMoney;
        this.gameState.totalMoneyEarned += earnedMoney;
        this.gameState.totalClicks++;
        
        this.showMoneyPopup(earnedMoney);
        this.playSound('click');
        this.addClickEffect();
        
        this.updateUI();
    }
    
    handlePrestige(layerId) {
        if (this.gameState.canPrestige(layerId)) {
            const gain = this.gameState.getPrestigeGain(layerId);
            const layer = this.gameState.prestigeLayers.find(l => l.id === layerId);
            
            if (confirm(`Reset progress for +${gain} ${layer.name}?\\n\\nThis will give you permanent bonuses but reset lower layers.`)) {
                this.gameState.doPrestige(layerId);
                this.updateUI();
                this.renderAllUpgrades();
                this.playSound('prestige');
            }
        }
    }
    
    showMoneyPopup(amount) {
        const popup = document.createElement('div');
        popup.className = 'money-popup';
        popup.textContent = `+$${this.gameState.formatNumber(amount)}`;
        
        const rect = this.elements.mainClickBtn.getBoundingClientRect();
        popup.style.left = (rect.left + Math.random() * rect.width) + 'px';
        popup.style.top = (rect.top + Math.random() * rect.height) + 'px';
        
        document.body.appendChild(popup);
        
        setTimeout(() => {
            popup.remove();
        }, 1000);
    }
    
    addClickEffect() {
        this.elements.mainClickBtn.classList.add('click-effect');
        setTimeout(() => {
            this.elements.mainClickBtn.classList.remove('click-effect');
        }, 200);
    }
    
    toggleSound() {
        this.gameState.settings.soundEnabled = !this.gameState.settings.soundEnabled;
        this.elements.soundToggle.textContent = this.gameState.settings.soundEnabled ? 'ON' : 'OFF';
        this.elements.soundToggle.className = this.gameState.settings.soundEnabled ? 
            'bg-game-primary text-white px-4 py-2 rounded' : 
            'bg-gray-400 text-white px-4 py-2 rounded';
    }
    
    toggleAutoSave() {
        this.gameState.settings.autoSaveEnabled = !this.gameState.settings.autoSaveEnabled;
        this.elements.autosaveToggle.textContent = this.gameState.settings.autoSaveEnabled ? 'ON' : 'OFF';
        this.elements.autosaveToggle.className = this.gameState.settings.autoSaveEnabled ? 
            'bg-game-secondary text-white px-4 py-2 rounded' : 
            'bg-gray-400 text-white px-4 py-2 rounded';
    }
    
    playSound(type) {
        if (!this.gameState.settings.soundEnabled) return;
        
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        switch (type) {
            case 'click':
                oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.1);
                gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
                oscillator.stop(audioContext.currentTime + 0.1);
                break;
            case 'upgrade':
                oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
                oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.2);
                gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
                oscillator.stop(audioContext.currentTime + 0.2);
                break;
            case 'prestige':
                oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
                oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.5);
                gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
                oscillator.stop(audioContext.currentTime + 0.5);
                break;
        }
        
        oscillator.start(audioContext.currentTime);
    }
    
    renderBasicUpgrades() {
        this.elements.basicUpgrades.innerHTML = '';
        
        this.gameState.basicUpgrades.forEach(upgrade => {
            const canAfford = this.gameState.money >= upgrade.cost;
            const maxed = upgrade.level >= upgrade.maxLevel;
            
            const upgradeElement = document.createElement('div');
            upgradeElement.className = `bg-white border rounded-lg p-4 transition-all duration-200 ${maxed ? 'opacity-50' : canAfford ? 'hover:shadow-md cursor-pointer' : 'opacity-60'}`;
            
            const typeIcon = upgrade.type === 'click' ? '👆' : upgrade.type === 'passive' ? '⚙️' : '✨';
            
            upgradeElement.innerHTML = `
                <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center">
                        <span class="text-2xl mr-2">${typeIcon}</span>
                        <div>
                            <h3 class="font-semibold text-gray-800">${upgrade.name}</h3>
                            <p class="text-sm text-gray-600">${upgrade.description}</p>
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="text-lg font-bold ${maxed ? 'text-green-500' : canAfford ? 'text-game-primary' : 'text-gray-400'}">
                            ${maxed ? 'MAX' : '$' + this.gameState.formatNumber(upgrade.cost)}
                        </div>
                        <div class="text-sm text-gray-500">Level ${upgrade.level}/${upgrade.maxLevel}</div>
                    </div>
                </div>
                <div class="text-sm text-gray-600">
                    ${upgrade.type === 'click' ? `+$${upgrade.effect} per click` : 
                      upgrade.type === 'passive' ? `+$${upgrade.effect}/s` : 
                      `${upgrade.effect}x multiplier`}
                </div>
            `;
            
            if (canAfford && !maxed) {
                upgradeElement.addEventListener('click', (e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    if (this.gameState.buyBasicUpgrade(upgrade.id)) {
                        this.playSound('upgrade');
                        this.updateUI();
                        this.renderBasicUpgrades();
                    }
                });
            }
            
            this.elements.basicUpgrades.appendChild(upgradeElement);
        });
    }
    
    renderBusinessUpgrades() {
        this.elements.businessUpgrades.innerHTML = '';
        
        this.gameState.businessUpgrades.forEach(business => {
            if (!business.unlocked) return;
            
            const canAfford = this.gameState.money >= business.cost;
            
            const businessElement = document.createElement('div');
            businessElement.className = `bg-white border rounded-lg p-4 transition-all duration-200 ${canAfford ? 'hover:shadow-md cursor-pointer' : 'opacity-60'}`;
            
            const tierIcons = ['🏪', '☕', '🍽️', '💻', '🏦', '🏢'];
            const icon = tierIcons[business.tier - 1] || '🏢';
            
            businessElement.innerHTML = `
                <div class="flex items-center justify-between">
                    <div class="flex items-center flex-1">
                        <span class="text-3xl mr-3">${icon}</span>
                        <div class="flex-1">
                            <h3 class="font-semibold text-gray-800">${business.name}</h3>
                            <p class="text-sm text-gray-600">${business.description}</p>
                            <div class="text-sm text-blue-600">Level ${business.level} • $${this.gameState.formatNumber(business.baseIncome)}/s each</div>
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="text-lg font-bold ${canAfford ? 'text-game-primary' : 'text-gray-400'}">
                            $${this.gameState.formatNumber(business.cost)}
                        </div>
                        <button class="buy-business-btn bg-game-primary text-white px-4 py-2 rounded text-sm font-bold mt-2 transition-all duration-200 ${!canAfford ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}"
                                ${!canAfford ? 'disabled' : ''}>
                            Buy +1
                        </button>
                    </div>
                </div>
            `;
            
            // Add click event to the button specifically
            const buyButton = businessElement.querySelector('.buy-business-btn');
            if (canAfford) {
                buyButton.addEventListener('click', (e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    if (this.gameState.buyBusinessUpgrade(business.id)) {
                        this.playSound('upgrade');
                        this.gameState.unlockBusinesses(); // Check for newly unlocked businesses
                        this.updateUI();
                        this.renderBusinessUpgrades();
                    }
                });
            }
            
            this.elements.businessUpgrades.appendChild(businessElement);
        });
    }
    
    renderPremiumUpgrades() {
        this.elements.premiumUpgrades.innerHTML = '';
        
        this.gameState.premiumUpgrades.forEach(upgrade => {
            const canAfford = this.gameState.gems >= upgrade.cost;
            
            const upgradeElement = document.createElement('div');
            upgradeElement.className = `bg-gradient-to-br from-purple-100 to-purple-200 border-2 border-purple-300 rounded-lg p-4 transition-all duration-200 ${canAfford ? 'hover:shadow-md cursor-pointer' : 'opacity-60'}`;
            
            const typeIcons = {
                'gem_income': '💎',
                'global_mult': '🌟',
                'prestige_boost': '🚀',
                'offline': '😴'
            };
            const icon = typeIcons[upgrade.type] || '✨';
            
            upgradeElement.innerHTML = `
                <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center">
                        <span class="text-2xl mr-2">${icon}</span>
                        <div>
                            <h3 class="font-semibold text-purple-800">${upgrade.name}</h3>
                            <p class="text-sm text-purple-600">${upgrade.description}</p>
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="text-lg font-bold ${canAfford ? 'text-purple-700' : 'text-gray-400'}">
                            💎${upgrade.cost}
                        </div>
                        <div class="text-sm text-purple-500">Level ${upgrade.level}</div>
                    </div>
                </div>
                <div class="text-sm text-purple-600">
                    ${upgrade.type === 'gem_income' ? `+${upgrade.effect}/s gems` :
                      upgrade.type === 'global_mult' ? `${upgrade.effect}x income` :
                      upgrade.type === 'prestige_boost' ? `${upgrade.effect}x prestige gain` :
                      `${upgrade.effect}x offline rate`}
                </div>
            `;
            
            if (canAfford) {
                upgradeElement.addEventListener('click', (e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    if (this.gameState.buyPremiumUpgrade(upgrade.id)) {
                        this.playSound('upgrade');
                        this.updateUI();
                        this.renderPremiumUpgrades();
                    }
                });
            }
            
            this.elements.premiumUpgrades.appendChild(upgradeElement);
        });
    }
    
    renderAllUpgrades() {
        this.renderBasicUpgrades();
        this.renderBusinessUpgrades();
        this.renderPremiumUpgrades();
    }
    
    updateUI() {
        // Update main displays
        this.elements.moneyDisplay.textContent = `$${this.gameState.formatNumber(this.gameState.money)}`;
        this.elements.mpsDisplay.textContent = `$${this.gameState.formatNumber(this.gameState.moneyPerSecond)}/s`;
        this.elements.clickValueDisplay.textContent = `$${this.gameState.formatNumber(this.gameState.clickValue)}`;
        this.elements.gemsDisplay.textContent = `💎 ${this.gameState.formatNumber(this.gameState.gems)}`;
        
        // Update prestige displays
        this.gameState.prestigeLayers.forEach((layer, index) => {
            const pointsElement = this.elements[`prestigePoints${index + 1}`];
            const btnElement = this.elements[`prestigeBtn${index + 1}`];
            
            if (pointsElement && btnElement) {
                pointsElement.textContent = this.gameState.formatNumber(layer.points);
                
                const canPrestige = this.gameState.canPrestige(layer.id);
                const gain = this.gameState.getPrestigeGain(layer.id);
                
                btnElement.disabled = !canPrestige;
                btnElement.textContent = canPrestige ? 
                    `Reset for +${gain} Points` : 
                    this.getPrestigeRequirementText(layer);
            }
        });
        
        // Update settings toggles
        this.elements.soundToggle.textContent = this.gameState.settings.soundEnabled ? 'ON' : 'OFF';
        this.elements.autosaveToggle.textContent = this.gameState.settings.autoSaveEnabled ? 'ON' : 'OFF';
    }
    
    getPrestigeRequirementText(layer) {
        switch (layer.requirementType) {
            case 'money':
                const needed = layer.requirement - this.gameState.totalMoneyEarned;
                return `Need $${this.gameState.formatNumber(needed)} more`;
            case 'business':
                const businessNeeded = layer.requirement - this.gameState.prestigeLayers[0].points;
                return `Need ${businessNeeded} Business Points`;
            case 'innovation':
                const innovationNeeded = layer.requirement - this.gameState.prestigeLayers[1].points;
                return `Need ${innovationNeeded} Innovation Points`;
            default:
                return 'Requirements not met';
        }
    }
    
    startGameLoop() {
        let lastUpgradeRefresh = Date.now();
        
        const gameLoop = () => {
            const now = Date.now();
            const deltaTime = (now - this.lastUpdate) / 1000;
            
            // Apply passive income
            if (this.gameState.moneyPerSecond > 0) {
                const earnings = this.gameState.moneyPerSecond * deltaTime;
                this.gameState.money += earnings;
                this.gameState.totalMoneyEarned += earnings;
            }
            
            // Generate gems periodically
            if (now - this.lastGemGeneration > 10000) { // Every 10 seconds
                const gemRate = this.gameState.generateGems();
                this.gameState.gems += gemRate * 0.01; // Small amount per check
                this.lastGemGeneration = now;
            }
            
            this.lastUpdate = now;
            
            // Check if we need to update business unlocks and recalculate values
            this.gameState.unlockBusinesses();
            this.gameState.calculateAll();
            this.updateUI();
            
            // Refresh upgrade displays every 2 seconds to update affordability
            if (now - lastUpgradeRefresh > 2000) {
                this.renderAllUpgrades();
                lastUpgradeRefresh = now;
            }
            
            requestAnimationFrame(gameLoop);
        };
        
        gameLoop();
    }
}

// Initialize the game when the page loads
document.addEventListener('DOMContentLoaded', () => {
    const game = new Game();
    game.renderAllUpgrades();
    
    // Make game globally accessible for debugging
    window.game = game;
});
